import type {ErrorTranslator, JsonFormsI18nState, JsonSchema} from '@jsonforms/core'
import {createCleanLabel} from '@jsonforms/core'
import type {ErrorObject, ValidateFunction} from 'ajv'
import Ajv2020 from 'ajv/dist/2020'
import addFormats from 'ajv-formats'

import {getNumericTypeErrorMessage} from './JobStepJsonFormRenderers/controls/JsonFormNumberControl.utils'

const createJobStepJsonSchemaValidator = () => {
  const ajv = new Ajv2020({
    strict: false,
    allErrors: true,
  })

  addFormats(ajv)

  return ajv
}

export const jobStepJsonSchemaValidator = createJobStepJsonSchemaValidator()

const translateRequiredError = (error: ErrorObject): string =>
  error.keyword === 'required' && error.params?.missingProperty
    ? `${createCleanLabel(error.params.missingProperty)} is required`
    : ''

/** AJV types its error params as `any` and reports a union type as a single `integer,string`. */
const getReportedTypes = (error: ErrorObject): readonly string[] => {
  const reportedType: unknown = error.params?.type

  if (Array.isArray(reportedType)) {
    return reportedType.filter((type): type is string => typeof type === 'string')
  }

  return typeof reportedType === 'string' ? reportedType.split(',') : []
}

// numeric parameters also accept a string to allow parameter references (see relaxNumericTypes),
// so AJV reports them as `must be integer,string`
const translateNumericTypeError = (error: ErrorObject): string => {
  if (error.keyword !== 'type') {
    return ''
  }

  const types = getReportedTypes(error)

  if (!types.includes('string')) {
    return ''
  }

  return getNumericTypeErrorMessage(types) ?? ''
}

export const translateSchemaError = (error: ErrorObject): string =>
  translateRequiredError(error) || translateNumericTypeError(error) || error.message || ''

const translateError: ErrorTranslator = error => translateSchemaError(error)

export const jsonFormsI18n: JsonFormsI18nState = {translateError}

const decodeJsonPointerSegment = (segment: string): string =>
  segment.replaceAll('~1', '/').replaceAll('~0', '~')

export const getSchemaErrorFieldName = (error: ErrorObject): string => {
  if (error.keyword === 'required' && error.params?.missingProperty) {
    return error.params.missingProperty
  }

  return error.instancePath.split('/').filter(Boolean).map(decodeJsonPointerSegment).at(-1) ?? ''
}

const headlessJsonSchemaValidator = createJobStepJsonSchemaValidator()
const compiledStepValidators = new WeakMap<object, ValidateFunction>()

export const getJsonSchemaErrors = (schema: JsonSchema, data: object): ErrorObject[] => {
  let validate = compiledStepValidators.get(schema)
  if (!validate) {
    try {
      validate = headlessJsonSchemaValidator.compile(schema)
    } catch {
      return []
    }
    compiledStepValidators.set(schema, validate)
  }

  validate(data)
  return validate.errors ?? []
}
